import type { GameContext } from "../GameContext";

export interface BootResult {
  manifestLoaded: boolean;
  degradedReason?: "unreachable" | "invalid_json" | "timeout";
}

/**
 * Pure controller logic for BootScene, kept separate from the Phaser.Scene
 * subclass so it's unit-testable without a canvas/WebGL context.
 *
 * Per 2.11's hard requirement: this NEVER blocks progression to the login
 * screen on a manifest failure. `run()` always resolves (never rejects)
 * and always ends with state transitioning to "unauthenticated" — the only
 * difference on manifest failure is `degradedReason` being set, which
 * BootScene uses to show a "some art may be missing" toast rather than a
 * blocking error screen.
 */
export class BootController {
  constructor(private readonly ctx: GameContext) {}

  async run(): Promise<BootResult> {
    const manifestResult = await this.ctx.assets.loadManifest();

    // Per StateManager's legal-transition table: boot -> unauthenticated
    // is always legal and is the correct destination regardless of asset
    // outcome, since this client never persists a token across page loads.
    this.ctx.state.transition("unauthenticated");

    if (manifestResult.ok) {
      return { manifestLoaded: true };
    }
    return { manifestLoaded: false, degradedReason: manifestResult.reason };
  }
}
